import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import YmdDateInput from "../ui/YmdDateInput";
import NoteMarkdownEditor from "../ui/NoteMarkdownEditor";
import InvoiceGlAllocationEditor from "./InvoiceGlAllocationEditor";
import { areGlAllocationsValid, formatGlAllocationsLabel } from "../../utils/glCodes";
import { getCurrentInvoiceMonthEndDate } from "../../utils/invoiceMonthDisplay";

const fieldClass =
  "w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 py-2 text-[13px] text-[var(--ink,#1c1c1a)] placeholder:text-[var(--faint,#9a9a95)] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)] disabled:bg-[var(--sunken,#eeece7)] disabled:cursor-not-allowed";
const numberClass = `${fieldClass} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;
const labelClass =
  "block text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1";
const sectionClass =
  "text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--ink,#1c1c1a)] mb-3";

const Field = ({ label, required, children }) => (
  <div>
    <label className={labelClass}>
      {label}
      {required ? <span className="text-[var(--accent,#a51c1c)]"> *</span> : null}
    </label>
    {children}
  </div>
);

const InvoiceRowEditPanel = ({
  invoice,
  editInvoiceDate,
  setEditInvoiceDate,
  editInvoiceNumber,
  setEditInvoiceNumber,
  setEditGlCode,
  editGlAllocations = [],
  setEditGlAllocations,
  editGlSplit = false,
  setEditGlSplit,
  glCodes = [],
  editMonthlyMrc,
  setEditMonthlyMrc,
  editTotalAmount,
  setEditTotalAmount,
  editCredit,
  setEditCredit,
  editTaxesAndSurcharges,
  setEditTaxesAndSurcharges,
  editDueDate,
  setEditDueDate,
  editNote,
  setEditNote,
  saving,
  generatingNote,
  onSave,
  onCancel,
  onGenerateNote,
}) => {
  const parsedMrc = parseFloat(editMonthlyMrc);
  const hasMrcAboveZero = !Number.isNaN(parsedMrc) && parsedMrc > 0;
  const busy = saving || generatingNote;
  const accountTitle = [invoice?.billing_account, invoice?.display_name]
    .filter(Boolean)
    .join(" · ");
  const glLabel = formatGlAllocationsLabel(
    invoice?.gl_allocations,
    invoice?.gl_code || "",
  );

  const handleMonthlyMrcChange = (next) => {
    setEditMonthlyMrc(next);
    const parsed = parseFloat(next);
    if (!Number.isNaN(parsed) && parsed > 0) {
      setEditCredit("0");
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !busy) onCancel?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [busy, onCancel]);

  const modal = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-5 bg-[#1c1c1a]/25 backdrop-blur-[4px]"
      style={{ WebkitBackdropFilter: "blur(4px)" }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-invoice-title"
        className="relative z-[1] flex max-h-[92vh] w-full max-w-[920px] flex-col overflow-hidden rounded-[12px] bg-white shadow-[0_24px_80px_rgba(28,28,26,0.28)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[var(--line,#e4e2dc)] px-6 py-4">
          <div className="min-w-0">
            <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#a51c1c]">
              Edit invoice
            </p>
            <h2
              id="edit-invoice-title"
              className="mt-1 truncate text-[22px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]"
            >
              {accountTitle || "Invoice"}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {invoice?.provider ? (
                <span className="inline-flex items-center rounded-full bg-[var(--sunken,#eeece7)] px-2 py-0.5 text-[12px] font-medium text-[var(--ink,#1c1c1a)]">
                  {invoice.provider}
                </span>
              ) : null}
              {glLabel ? (
                <span className="inline-flex items-center rounded-full bg-[var(--sunken,#eeece7)] px-2 py-0.5 text-[12px] font-medium text-[var(--ink,#1c1c1a)]">
                  {glLabel}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="shrink-0 rounded-md p-1 text-[26px] leading-none text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] hover:text-[var(--ink,#1c1c1a)] disabled:opacity-50"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <p className={sectionClass}>Invoice details</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Invoice date" required>
              <YmdDateInput
                value={editInvoiceDate}
                onChange={(e) => setEditInvoiceDate(e.target.value)}
                max={getCurrentInvoiceMonthEndDate()}
                disabled={saving}
                className={fieldClass}
                aria-label="Invoice date"
              />
            </Field>
            <Field label="Invoice number" required>
              <input
                type="text"
                value={editInvoiceNumber}
                onChange={(e) => setEditInvoiceNumber(e.target.value)}
                disabled={saving}
                className={fieldClass}
                aria-label="Invoice number"
                placeholder="Enter invoice number"
              />
            </Field>
            <Field label="Due date">
              <YmdDateInput
                value={editDueDate}
                onChange={(e) => setEditDueDate(e.target.value)}
                disabled={saving}
                className={fieldClass}
                aria-label="Due Date"
              />
            </Field>
          </div>

          <p className={`${sectionClass} mt-6`}>Amounts</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Monthly MRC">
              <input
                type="number"
                inputMode="decimal"
                step="any"
                value={editMonthlyMrc}
                onChange={(e) => handleMonthlyMrcChange(e.target.value)}
                onWheel={(e) => e.target.blur()}
                disabled={saving}
                className={numberClass}
                aria-label="Monthly MRC"
              />
            </Field>
            <Field label="Total invoice amount">
              <input
                type="number"
                inputMode="decimal"
                step="any"
                value={editTotalAmount}
                onChange={(e) => setEditTotalAmount(e.target.value)}
                onWheel={(e) => e.target.blur()}
                disabled={saving}
                className={numberClass}
                aria-label="Total invoice amount"
              />
            </Field>
            <Field label="Credit">
              <input
                type="number"
                inputMode="decimal"
                step="any"
                min="0"
                value={hasMrcAboveZero ? "0" : editCredit}
                onChange={(e) => setEditCredit(e.target.value)}
                onWheel={(e) => e.target.blur()}
                disabled={saving || hasMrcAboveZero}
                className={numberClass}
                aria-label="Credit"
                placeholder="0.00"
              />
            </Field>
            <Field label="Taxes and surcharges">
              <input
                type="number"
                inputMode="decimal"
                step="any"
                value={editTaxesAndSurcharges}
                onChange={(e) => setEditTaxesAndSurcharges(e.target.value)}
                onWheel={(e) => e.target.blur()}
                disabled={saving}
                className={numberClass}
                aria-label="Taxes and Surcharges"
                placeholder="0.00"
              />
            </Field>
          </div>

          <p className={`${sectionClass} mt-6`}>GL allocation</p>
          <InvoiceGlAllocationEditor
            className=""
            glCodes={glCodes}
            allocations={editGlAllocations}
            onAllocationsChange={(next) => {
              if (typeof setEditGlAllocations === "function") setEditGlAllocations(next);
              if (typeof setEditGlCode === "function") {
                setEditGlCode(String(next[0]?.gl_code || ""));
              }
            }}
            splitEnabled={editGlSplit}
            onSplitChange={setEditGlSplit}
            totalAmount={editTotalAmount}
            disabled={saving}
          />

          <p className={`${sectionClass} mt-6`}>Note</p>
          <NoteMarkdownEditor
            value={editNote}
            onChange={setEditNote}
            disabled={saving || generatingNote}
            className="resize-y"
            minHeightClass="min-h-[4.5rem]"
            aria-label="Note"
            placeholder="Optional note"
          />
          {onGenerateNote ? (
            <button
              type="button"
              onClick={onGenerateNote}
              disabled={busy}
              className="relative mt-2 inline-flex items-center justify-center gap-1 rounded-full px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50 group"
              style={{ background: "linear-gradient(135deg, #2dd4bf, #3b82f6, #a855f7)" }}
            >
              <span className="absolute inset-[1.5px] rounded-full bg-white transition-colors duration-150 group-hover:bg-slate-50" />
              <span className="relative inline-flex items-center gap-1">
                {generatingNote ? (
                  <svg className="h-3 w-3 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                ) : (
                  <svg className="h-3 w-3 text-slate-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9.813 2.659a1.5 1.5 0 012.374 0l1.22 1.84a1 1 0 00.757.424l2.2.255a1.5 1.5 0 01.831 2.548l-1.59 1.55a1 1 0 00-.287.884l.375 2.186a1.5 1.5 0 01-2.175 1.58l-1.967-.965a1 1 0 00-.878 0l-1.967.965a1.5 1.5 0 01-2.175-1.58l.375-2.186a1 1 0 00-.287-.884l-1.59-1.55a1.5 1.5 0 01.831-2.548l2.2-.255a1 1 0 00.757-.424l1.22-1.84z" />
                  </svg>
                )}
                <span className="text-[12px] font-semibold tracking-wide text-slate-600">
                  {generatingNote ? "Generating…" : "Generate note"}
                </span>
              </span>
            </button>
          ) : null}
        </div>

        <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-[var(--line,#e4e2dc)] px-6 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center justify-center rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={busy || !areGlAllocationsValid(editGlAllocations)}
            className="inline-flex items-center justify-center rounded-md bg-[var(--accent,#a51c1c)] px-4 py-2 text-[13px] font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default InvoiceRowEditPanel;
